import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'
import type { CronAnswer } from './cronAnswer'

/**
 * Settings → Easeedesk → General settings.
 *
 * Perechile vechi: `settings/easeedesk/ticket_general_settings`,
 * `get_parsing_accounts`, `new_parsing_account`, `email_parsing_settings`,
 * `remove_parsing_account`, `update_services`, `generatePairKey`,
 * `sms_conversation_settings`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface ServiceRow {
  ID: number
  service_name: string
  enabled: number
}

export interface TicketGeneralSettings {
  title: string
  moduleUrl: string
  services: ServiceRow[]
  sms_pair_key: string
}

export interface ParsingAccount {
  ID: number
  /** 1 = IMAP, 2 = Microsoft Graph. */
  connection_type: number
  server_path: string
  port: string
  mailbox: string
  username: string
  app_client_id: string
  app_tenant_id: string
  app_secret_key: string
  activate: number
  enable_sender_email: number
  email_body: string
  /** Parola nu iese din server; ecranul stie doar daca exista una. */
  has_password: boolean
}

const BASE = '/settings/easeedesk/ticket_general_settings'

export function ticketGeneralSettingsQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_general_settings'],
    queryFn: () => apiGet<TicketGeneralSettings>(BASE),
  })
}

export function parsingAccountsQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'ticket_general_settings', 'parsing'],
    queryFn: () => apiGet<{ accounts: ParsingAccount[] }>(`${BASE}/parsing`),
  })
}

export function newParsingAccount() {
  return apiPost<{ ok?: boolean; ID?: number }>(`${BASE}/parsing`, {})
}

export interface ParsingAccountForm {
  connection_type: number
  server_path: string
  port: string
  mailbox: string
  parsing_username: string
  /** Gol sau `********` inseamna "nu se atinge parola din baza". */
  parsing_password: string
  app_client_id: string
  app_tenant_id: string
  app_secret_key: string
  activate_parsing: boolean
  enable_sender_email: boolean
  email_body: string
}

/*
 * Salvarea / stergerea unui cont de parsare anunta si cronul de citit
 * e-mail. `cron: 'failed'` cu `cron_message` = contul e salvat, dar cronul
 * n-a fost anuntat, si de ce.
 */
export function saveParsingAccount(account: number, form: ParsingAccountForm) {
  return apiPost<CronAnswer>(`${BASE}/parsing/${account}`, {
    connection_type: form.connection_type,
    server_path: form.server_path,
    port: form.port,
    mailbox: form.mailbox,
    parsing_username: form.parsing_username,
    parsing_password: form.parsing_password,
    app_client_id: form.app_client_id,
    app_tenant_id: form.app_tenant_id,
    app_secret_key: form.app_secret_key,
    activate_parsing: form.activate_parsing ? 1 : 0,
    enable_sender_email: form.enable_sender_email ? 1 : 0,
    email_body: form.email_body,
  })
}

export function removeParsingAccount(account: number) {
  return apiPost<CronAnswer>(`${BASE}/parsing/${account}/remove`, {})
}

export function saveServices(serviceIds: number[]) {
  return apiPost<{ ok?: boolean }>(`${BASE}/services`, { 'service[]': serviceIds })
}

export function generatePairKey() {
  return apiGet<{ pair_key: string }>(`${BASE}/pair_key`)
}

export function saveSmsPairKey(pairKey: string) {
  return apiPost<{ ok?: boolean; pair_key?: string }>(`${BASE}/sms`, { pair_key: pairKey })
}

/** O parte a refacerii: un hash din Redis (baza 7), cate campuri a primit, si daca a mers. */
export interface RebuildPart {
  part: string
  hash: string
  fields: number
  ok: boolean
  error?: string
}

export interface RebuildResult {
  ok: boolean
  parts: RebuildPart[]
  /** Numele partilor care n-au mers; celelalte s-au scris oricum. */
  errors: string[]
}

/**
 * "Rebuild configuration" - `rebuild_configuration` din portal: reface
 * hash-urile Easeedesk din Redis din care citesc crearea tichetelor (ph4.api)
 * si lista de tichete. Cere `settings.easeedesk.read`, ca in CI3.
 */
export function rebuildConfiguration() {
  return apiPost<RebuildResult>(`${BASE}/rebuild_configuration`, {})
}
